// src/pages/ForgotPasswordPage.tsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { forgotPasswordApi, resetPasswordApi } from "../api/auth";

type PasswordRule =
  | "minLength"
  | "uppercase"
  | "lowercase"
  | "digit"
  | "special";

function validatePassword(password: string): PasswordRule[] {
  const errors: PasswordRule[] = [];

  if (password.length < 8) {
    errors.push("minLength");
  }
  if (!/[A-Z]/.test(password)) {
    errors.push("uppercase");
  }
  if (!/[a-z]/.test(password)) {
    errors.push("lowercase");
  }
  if (!/[0-9]/.test(password)) {
    errors.push("digit");
  }
  if (!/[^A-Za-z0-9]/.test(password)) {
    errors.push("special");
  }

  return errors;
}

const ForgotPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const [step, setStep] = useState<"request" | "reset">("request");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const passwordErrors = validatePassword(password);
  const passwordsMatch = password === confirmPassword || confirmPassword === "";

  const handleRequestCode: React.FormEventHandler = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      const res = await forgotPasswordApi(email.trim());
      setSuccess(
        res.message ||
          t(
            "forgotPassword.success.requested",
            "If an account exists, we sent a reset code to your email."
          )
      );
      setStep("reset");
    } catch (err: any) {
      console.error(err);
      setError(
        err?.message ||
          t(
            "forgotPassword.errors.requestFailed",
            "We couldn't send the reset code. Please try again."
          )
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword: React.FormEventHandler = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!passwordsMatch) {
      setError(
        t(
          "forgotPassword.errors.mismatch",
          "Passwords do not match."
        )
      );
      return;
    }

    if (passwordErrors.length > 0) {
      setError(
        t(
          "forgotPassword.errors.requirements",
          "Password does not meet the requirements."
        )
      );
      return;
    }

    setLoading(true);
    try {
      const res = await resetPasswordApi(
        email.trim(),
        code.trim(),
        password
      );
      setSuccess(
        res.message ||
          t(
            "forgotPassword.success.reset",
            "Password updated successfully. You can log in now."
          )
      );
      setPassword("");
      setConfirmPassword("");
      setCode("");
    } catch (err: any) {
      console.error(err);
      setError(
        err?.message ||
          t(
            "forgotPassword.errors.resetFailed",
            "Invalid or expired code. Please try again."
          )
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="auth-landing">
      <div className="lp-container">
        <div className="auth-layout">
          <div className="auth-copy">
            <h1 className="auth-title">
              {t("forgotPassword.title", "Recover your password")}
            </h1>
            <p className="auth-subtitle">
              {step === "request"
                ? t(
                    "forgotPassword.subtitle.request",
                    "Enter the email you used to register. We'll send you a 6-digit code."
                  )
                : t(
                    "forgotPassword.subtitle.reset",
                    "Enter the code we emailed you and choose a new password."
                  )}
            </p>
            <ul className="auth-bullets">
              <li>
                {t(
                  "forgotPassword.bullets.b1",
                  "Use the same email you registered with."
                )}
              </li>
              <li>
                {t(
                  "forgotPassword.bullets.b2",
                  "Codes expire in 10 minutes."
                )}
              </li>
              <li>
                {t(
                  "forgotPassword.bullets.b3",
                  "You'll choose a new password after verifying the code."
                )}
              </li>
            </ul>
          </div>

          <div className="auth-card">
              {step === "request" ? (
                <form className="form auth-form" onSubmit={handleRequestCode}>
                  {error && (
                    <div className="form-error auth-form-error">
                      {error}
                    </div>
                  )}
                  {success && (
                    <div className="form-success onboarding-form-success-txt auth-form-success">
                      {success}
                    </div>
                  )}

                  <label className="form-field">
                    <span>{t("forgotPassword.emailLabel", "Email")}</span>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </label>

                  <button
                    className="lp-btn lp-btn-primary auth-submit"
                    type="submit"
                    disabled={loading}
                  >
                    {loading
                      ? t(
                          "forgotPassword.actions.sending",
                          "Sending..."
                        )
                      : t(
                          "forgotPassword.actions.sendCode",
                          "Send reset code"
                        )}
                  </button>

                  <p className="auth-switch">
                    {t("forgotPassword.backToLogin", "Back to")}{" "}
                    <Link to="/login">{t("login.pageTitle", "Login")}</Link>
                  </p>
                </form>
              ) : (
                <form className="form auth-form" onSubmit={handleResetPassword}>
                  {error && (
                    <div className="form-error auth-form-error">
                      {error}
                    </div>
                  )}
                  {success && (
                    <div className="form-success onboarding-form-success-txt auth-form-success">
                      {success}
                    </div>
                  )}

                  <label className="form-field">
                    <span>{t("forgotPassword.emailLabel", "Email")}</span>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </label>

                  <label className="form-field">
                    <span>{t("forgotPassword.codeLabel", "Reset code")}</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="\d*"
                      maxLength={6}
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      required
                    />
                  </label>

                  <label className="form-field">
                    <span>{t("forgotPassword.newPassword", "New password")}</span>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                  </label>

                  <div className="password-requirements">
                    <p className="pw-title">
                      {t("register.pwTitle")}
                    </p>
                    <ul>
                      <li
                        className={
                          passwordErrors.includes("minLength")
                            ? "pw-rule pw-rule-bad"
                            : "pw-rule pw-rule-ok"
                        }
                      >
                        {t("register.pwRules.minLength")}
                      </li>
                      <li
                        className={
                          passwordErrors.includes("uppercase")
                            ? "pw-rule pw-rule-bad"
                            : "pw-rule pw-rule-ok"
                        }
                      >
                        {t("register.pwRules.uppercase")}
                      </li>
                      <li
                        className={
                          passwordErrors.includes("lowercase")
                            ? "pw-rule pw-rule-bad"
                            : "pw-rule pw-rule-ok"
                        }
                      >
                        {t("register.pwRules.lowercase")}
                      </li>
                      <li
                        className={
                          passwordErrors.includes("digit")
                            ? "pw-rule pw-rule-bad"
                            : "pw-rule pw-rule-ok"
                        }
                      >
                        {t("register.pwRules.digit")}
                      </li>
                      <li
                        className={
                          passwordErrors.includes("special")
                            ? "pw-rule pw-rule-bad"
                            : "pw-rule pw-rule-ok"
                        }
                      >
                        {t("register.pwRules.special")}
                      </li>
                    </ul>
                  </div>

                  <label className="form-field">
                    <span>
                      {t("forgotPassword.confirmPassword", "Confirm password")}
                    </span>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                    />
                  </label>
                  {!passwordsMatch && (
                    <div className="field-error">
                      {t("register.errorPasswordMismatch")}
                    </div>
                  )}

                  <button
                    className="lp-btn lp-btn-primary auth-submit"
                    type="submit"
                    disabled={loading}
                  >
                    {loading
                      ? t(
                          "forgotPassword.actions.updating",
                          "Updating..."
                        )
                      : t(
                          "forgotPassword.actions.resetPassword",
                          "Reset password"
                        )}
                  </button>

                  <p className="auth-switch">
                    {t("forgotPassword.backToLogin", "Back to")}{" "}
                    <Link to="/login">{t("login.pageTitle", "Login")}</Link>
                  </p>
                </form>
              )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ForgotPasswordPage;
